import React, { useState, useEffect } from "react";
import { Row, Col, Form } from "react-bootstrap";
import Card from "../../../components/Card";
import { getApiCall } from "../../../service/httpCall";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { LICENCE_HISTORY_LIST } from "../../../service/url";
import Loader from "../../../components/partials/components/loader";
import exportData from "../../../utilities/export";
import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import { errorCheck } from "../auth/auth-utils";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import "../../../assets/custom/css/custom.css";
import { LICENCE_LABEL } from "../../../utilities/constant";
import { Download, ArrowUpCircle, FileText, User, Building2 } from "lucide-react";
import {
  FaBroadcastTower,
  FaBuilding,
  FaUsers,
  FaUserCheck,
  FaPhone,
  FaArrowRight
} from "react-icons/fa";

const ICON_MAP = {
  channel: <FaBroadcastTower />,
  company: <FaBuilding />,
  live_agent: <FaUserCheck />,
  role_ccagent: <FaUsers />,
  role_ccadmin: <FaPhone />,
};

export default function LicenceHistoryList() {
  const navigate = useNavigate();
  const [operationList, setOperationList] = useState([]);
  const [originalDataList, setOriginalDataList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [buttonDisable, setButtonDisable] = useState(false);
  const [searchValue, setSearchValue] = useState("");

  const handleExport = async () => {
    setButtonDisable(true);
    await exportData("LicenceHistory", setToastProps, setShowToast);
    setButtonDisable(false);
  };

  const [showToast, setShowToast] = useState(false);
  const [toastProps, setToastProps] = useState(null);

  useEffect(() => {
    setLoading(true);
    getApiCall(LICENCE_HISTORY_LIST)
      .then(onOperationListSuccess)
      .catch(onError)
      .finally(() => setLoading(false));
  }, []);

  function onOperationListSuccess(resp) {
    const updatedList = resp.map((val, idx) => {
      return {
        ...val,
        key: idx + 1,
        created_at_formatted: new Date(val.created_at).toLocaleString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
        }),
      };
    });

    setOperationList(updatedList);
    setOriginalDataList(updatedList);
  }

  function onError(err) {
    if (typeof err !== "object" && err.includes("TokenExpiredError")) {
      errorCheck(err, navigate, setToastProps, setShowToast);
    } else {
      console.log(err);
    }
  }

  const licenceChangeBody = (rowData) => {
    const oldLic = rowData.old_licence || {};
    const newLic = rowData.updated_licence || {};

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
        {Object.keys(LICENCE_LABEL).filter((key) => {
          if (rowData.callcenter && key === "role_ccadmin") return false;
          if (rowData.callcenter && key === "company") return false;
          return true;
        }).map((key) => {
          const oldVal = oldLic[key] ?? 0;
          const newVal = newLic[key] ?? 0;
          const changed = oldVal !== newVal;
          const changeClassName =
            newVal > oldVal
              ? "licence-change-card licence-change-up"
              : newVal < oldVal
                ? "licence-change-card licence-change-down"
                : "licence-change-card";

          return (
            <div
              key={key}
              className={changeClassName}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderRadius: "8px",
                padding: "6px 10px",
                fontSize: "13px",
              }}
            >
              {/* Left */}
              <div
                className="licence-change-label"
                style={{ display: "flex", alignItems: "center", gap: "6px", fontWeight: 600, marginRight: "3px" }}
              >
                <span style={{ color: "#6366f1", fontSize: "14px" }}>
                  {ICON_MAP[key]}
                </span>
                {LICENCE_LABEL[key]}
              </div>

              {/* Right */}
              <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                <span
                  className="licence-change-value"
                  style={{
                    background: "#fee2e2",
                    color: "#dc2626",
                    padding: "2px 6px",
                    borderRadius: "6px",
                    fontWeight: 600,
                  }}
                >
                  {oldVal}
                </span>

                <span className="licence-change-arrow">
                  <FaArrowRight size={14} color="currentColor" />
                </span>

                <span
                  className="licence-change-value"
                  style={{
                    background: "#dcfce7",
                    color: "#16a34a",
                    padding: "2px 6px",
                    borderRadius: "6px",
                    fontWeight: 600,
                  }}
                >
                  {newVal}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  useEffect(() => {
    if (!searchValue.trim()) {
      setOperationList(originalDataList);
      return;
    }

    const lowerSearch = searchValue.toLowerCase();

    const filtered = originalDataList.filter((item) => {
      
      if (item.action?.toLowerCase().includes(lowerSearch)) return true;
      if (item.user?.username?.toLowerCase().includes(lowerSearch)) return true;
      if (item.company?.name?.toLowerCase().includes(lowerSearch)) return true;
      if (item.callcenter?.name?.toLowerCase().includes(lowerSearch)) return true;
      if (item.created_at_formatted?.toLowerCase().includes(lowerSearch)) return true;

      const oldLic = item.old_licence || {};
      const newLic = item.updated_licence || {};

      for (const key of Object.keys(LICENCE_LABEL)) {
        if (String(oldLic[key] ?? "").toLowerCase().includes(lowerSearch)) return true;
        if (String(newLic[key] ?? "").toLowerCase().includes(lowerSearch)) return true;
      }

      return false;
    });

    setOperationList(filtered);
  }, [searchValue, originalDataList]);

  if (loading) {
    return <Loader />;
  }

  return (
    <>
      <Row>
        <Col sm="12">
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div className="header-title">
                <h4 className="card-title">Licence History</h4>
              </div>
            </Card.Header>
            <Card.Body className="px-0">
              <div className="d-flex justify-content-between align-items-center mb-3 px-3 flex-wrap gap-3">
                <div className="flex-grow-1" style={{ maxWidth: "400px" }}>
                  <Form.Control
                    type="text"
                    placeholder="Search values..."
                    value={searchValue}
                    onChange={(e) => setSearchValue(e.target.value)}
                  />
                </div>

                {operationList.length > 0 && (
                  <Button
                    onClick={handleExport}
                    className="p-button-active p-button-sm"
                    disabled={buttonDisable}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "6px",
                      padding: "8px 16px",
                    }}
                  >
                    {buttonDisable ? (
                      <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                    ) : (
                      <Download size={16} />
                    )}
                    Export Data
                  </Button>
                )}
              </div>

              <div
                className="table-responsive broad-scrollbar"
                style={{ overflowX: "auto" }}
              >
                <DataTable
                  value={operationList}
                  paginator
                  rows={10}
                  emptyMessage="No licence history found."
                  tableStyle={{ minWidth: "50rem" }}
                >
                  <Column field="key" header="#" style={{ width: "5%" }} />

                  <Column
                    header="Action"
                    body={(rowData) => {
                      const isCreated = rowData.action?.toLowerCase() === "created";
                      return (
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "4px 10px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: 500,
                            backgroundColor: isCreated ? "#3b82f6" : "#10b981",
                            color: "white",
                          }}
                        >
                          {isCreated ? (
                            <FileText size={14} />
                          ) : (
                            <ArrowUpCircle size={14} />
                          )}
                          {rowData.action?.toUpperCase()}
                        </span>
                      );
                    }}
                    style={{ width: "10%" }}
                  />

                  <Column
                    header="Changes"
                    body={licenceChangeBody}
                    style={{ width: "50%" }}
                  />

                  <Column
                    header="Username"
                    body={(rowData) => (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        <User size={16} style={{ color: "#6b7280", flexShrink: 0 }} />
                        <span>{rowData.user?.username || "-"}</span>
                      </div>
                    )}
                    style={{ width: "20%" }}
                  />
                  <Column
                    header="Company"
                    body={(rowData) => (
                      <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                        {rowData.company?.name ? (
                          <>
                            <Building2 size={16} style={{ color: "#6b7280", flexShrink: 0 }} />
                            <span>{rowData.company.name}</span>
                          </>
                        ) : (
                          <span>-</span>
                        )}
                      </div>
                    )}
                    style={{ width: "20%" }}
                  />
                  <Column
                    field="callcenter.name"
                    header="Call Center"
                    style={{ width: "20%" }}
                  />

                  <Column
                    field="created_at_formatted"
                    header="Date & Time"
                    style={{ width: "25%" }}
                  />
                </DataTable>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <style>
        {`
          .broad-scrollbar::-webkit-scrollbar {
            height: 10px;
          }
          .broad-scrollbar::-webkit-scrollbar-thumb {
            background-color: #888;
            border-radius: 10px;
          }
        `}
      </style>
    </>
  );
}
